import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { describeError, useToast } from "./toast";
import { Trans, useTranslation } from "react-i18next";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import { useState } from "react";
import type { PairingCode } from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import { useAuth } from "../auth/AuthContext";
import { PermissionGate } from "../auth/access";
import { useFormat } from "../i18n/format";
import { errorText } from "../utils/apiError";

interface Props {
  open: boolean;
  premiseId: string;
  premiseName: string;
  onClose: () => void;
  onCreated?: (code: PairingCode) => void;
}

export default function PairTVDialog({
  open,
  premiseId,
  premiseName,
  onClose,
  onCreated,
}: Props) {
  const { user } = useAuth();
  const [days, setDays] = useState(1);
  const [issued, setIssued] = useState<PairingCode | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const toast = useToast();
  const { t } = useTranslation(["premises", "common"]);
  const fmt = useFormat();

  function reset() {
    setIssued(null);
    setDays(1);
    setErr(null);
    setBusy(false);
  }

  async function generate() {
    setBusy(true);
    setErr(null);
    try {
      const c = await foyerApi.createPairing({
        premiseId,
        expiryDays: days,
        // Stamp the code with the logged-in user - no separate input.
        createdBy: user?.user ?? "admin",
      });
      setIssued(c);
      toast.success(
        t("pairTVDialog.created", { code: c.code, premise: premiseName })
      );
      onCreated?.(c);
    } catch (e) {
      setErr(errorText(e, t));
      toast.error(
        describeError(t, "createInstallationCode", e, {
          premise: premiseName,
        })
      );
    } finally {
      setBusy(false);
    }
  }

  function copy() {
    if (!issued) return;
    const code = issued.code;
    navigator.clipboard
      .writeText(code)
      .then(() => toast.success(t("pairTVDialog.copied", { code })))
      .catch(() => toast.error(t("pairTVDialog.copyFailed", { code })));
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose();
        reset();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t("pairTVDialog.title")}</DialogTitle>
      <DialogContent>
        {!issued ? (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <Trans
                t={t}
                i18nKey="pairTVDialog.intro"
                values={{
                  premise: premiseName,
                  user: user?.user ?? t("pairTVDialog.unknownUser"),
                }}
                components={{ strong: <strong /> }}
              />
            </Typography>
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "fontWeightMedium" }}
                >
                  {t("pairTVDialog.validFor")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("pairTVDialog.days", { count: days })}
                </Typography>
              </Stack>
              <Slider
                value={days}
                min={1}
                max={14}
                step={1}
                marks
                onChange={(_, v) => setDays(v as number)}
                data-testid="pairing-days"
              />
            </Box>
            {err && (
              <Typography variant="body2" color="error">
                {err}
              </Typography>
            )}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 1, alignItems: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {t("pairTVDialog.enterPin", { premise: premiseName })}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              data-testid="pairing-code-display"
            >
              <Typography
                variant="h2"
                sx={{
                  fontFamily: "fontFamilyMono",
                  fontWeight: "fontWeightSemibold",
                  letterSpacing: "0.15em",
                }}
              >
                {issued.code}
              </Typography>
              <Tooltip title={t("common:buttons.copy")}>
                <IconButton
                  onClick={copy}
                  aria-label={t("pairTVDialog.copyPairingCode")}
                  data-testid="pairing-copy"
                >
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack
              direction="row"
              spacing={3}
              sx={{ color: "text.secondary", mt: 1 }}
            >
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" sx={{ display: "block" }}>
                  {t("pairTVDialog.createdBy")}
                </Typography>
                <Typography variant="body2">
                  {issued.createdBy ?? "admin"}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="caption" sx={{ display: "block" }}>
                  {t("pairTVDialog.expires")}
                </Typography>
                <Typography variant="body2">
                  {fmt.dateTime(issued.expiresAt)}
                </Typography>
              </Box>
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onClose();
            reset();
          }}
        >
          {issued ? t("common:buttons.done") : t("common:buttons.cancel")}
        </Button>
        {!issued && (
          <PermissionGate
            resource="screens"
            level="administrate"
            place={{ premiseId }}
          >
            <Button
              variant="contained"
              onClick={generate}
              disabled={busy}
              data-testid="pairing-generate"
            >
              {busy ? t("pairTVDialog.generating") : t("pairTVDialog.generate")}
            </Button>
          </PermissionGate>
        )}
      </DialogActions>
    </Dialog>
  );
}
